import { Box, Flex } from "@chakra-ui/layout";
import useDeleteArquivo from "features/documentos/hooks/useDeleteArquivo";
import useDocumentosGeral from "features/documentos/hooks/useDocumentos";
import usePastasGeral from "features/documentos/hooks/usePastas";
import { showConfirm, showErrorAxios } from "infra/helpers/alerta";
import { useEffect, useState } from "react";
import SelectGrupoUnidade from "components/Input/SelectGrupoUnidade";
import SelectUnidade from "components/Input/SelectUnidade";
import PageHeader from "components/Page/PageHeader";
import Documentos from "./components/Documentos";
import ModalDocumento from "./components/ModalDocumento";
import ModalPasta from "./components/ModalPasta";
import Pastas from "./components/Pastas";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import OutilineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";

import { UseQueryResult, UseMutationResult } from "react-query";
import {
  PastaItem,
  PastaForm,
  DocumentoForm,
  UseDocumentosParams,
  UsePastasParams,
  UseSaveDocumentosProps,
  DocumentoItem,
  UseSavePastaProps,
} from "features/documentos/typings";
import { PaginateResult } from "resources/types/shared";
import { AxiosResponse } from "axios";
import Pagination from "components/Pagination";
import { PermissaoEnum } from "resources/enums/permissao";
import Container from "components/Container";
import InputSearch from "ui/components/Input/InputSearch";
import BreadcrumbDocs from "./components/BreadcrumbDocs";

interface ContentDocumentosProps {
  title: string;
  midComponent: boolean;
  unidadeId?: string;
  hideDestino?: boolean;
  usePastas?: (params: UsePastasParams) => UseQueryResult<PastaItem[], unknown>;
  useDocumentos?: (
    params: UseDocumentosParams
  ) => UseQueryResult<PaginateResult<DocumentoItem>, unknown>;
  useSaveDocumento?: (
    props: UseSaveDocumentosProps
  ) => UseMutationResult<AxiosResponse<any>, unknown, DocumentoForm, unknown>;
  useSavePasta?: (
    props: UseSavePastaProps
  ) => UseMutationResult<AxiosResponse<any>, unknown, PastaForm, unknown>;
}

function ContentDocumentos({
  title,
  midComponent,
  unidadeId,
  hideDestino,
  usePastas = usePastasGeral,
  useDocumentos = useDocumentosGeral,
  useSavePasta,
  useSaveDocumento,
}: ContentDocumentosProps) {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [unidadesIds, setUnidadesIds] = useState<string[]>([]);
  const [gruposUnidadesIds, setGrupoUnidadesIds] = useState<string[]>([]);
  const [busca, setBusca] = useState<string>("");
  const [modalCreatePastaVisible, setModalCreatePastaVisible] = useState<{
    visible: boolean;
    documentoPaiID?: string;
  }>({
    visible: false,
  });
  const [editPasta, setEditPasta] = useState<{
    nome: string;
    id: string;
    tipoDestinoId?: TipoDestinoEnum | null;
    destinoId?: string;
  }>();
  const [modalAddDocumentoVisible, setModalAddDocumentoVisible] =
    useState(false);
  const [editDocumentoId, setEditDocumentoId] = useState<string | undefined>();
  const { mutate: deletarArquivo, isLoading: isLoadingDeletarArquivo } =
    useDeleteArquivo();

  const [selectedPastaId, setSelectedPastaId] = useState<string | undefined>();
  const [breadCrumb, setBreadCrumb] = useState<{ id: string; name: string }[]>([
    { name: "Todos", id: "" },
  ]);

  function onSelectPasta(id?: string) {
    if (id?.length) {
      const positionInBreadCrumb = breadCrumb.findIndex((v) => v.id === id);
      if (positionInBreadCrumb > -1) {
        setBreadCrumb((v) => [...v.slice(0, positionInBreadCrumb + 1)]);
      } else {
        setBreadCrumb((v) => [
          ...v,
          { id, name: pastas?.find((p) => p.id === id)?.nome! },
        ]);
      }
    } else {
      setBreadCrumb([{ name: "Todos", id: "" }]);
    }
    setPagination((state) => ({ ...state, page: 1 }));
    setSelectedPastaId(id);
  }
  const {
    data: pastas,
    refetch: refetchPastas,
    isLoading: isLoadingPastas,
    isFetching: isFetchingPastas,
  } = usePastas({
    unidadeId,
    unidadesIds,
    gruposUnidadesIds,
    pastaPaiId: selectedPastaId,
  });

  const {
    data: documentos,
    refetch: refetchDocumentos,
    isLoading: isLoadingDocumentos,
    isFetching: isFetchingDocumentos,
  } = useDocumentos({
    documentoPaiId: selectedPastaId,
    gruposUnidadesIds,
    unidadesIds,
    unidadeId,
    page: pagination.page,
    limit: pagination.limit,
    search: busca,
  });


  const nomePasta = selectedPastaId
    ? pastas?.find((p) => p.id === selectedPastaId)?.nome
    : "Todos";

  const _isLoadingPastas = isLoadingPastas || isFetchingPastas;
  const _isLoadingDocumentos =
    isLoadingDocumentos || isFetchingDocumentos || isLoadingDeletarArquivo;

  useEffect(() => {
    if (editPasta) {
      setModalCreatePastaVisible({
        visible: true,
        documentoPaiID: selectedPastaId,
      });
    }
  }, [editPasta, selectedPastaId]);

  useEffect(() => {
    if (!modalCreatePastaVisible) {
      setEditPasta(undefined);
    }
  }, [modalCreatePastaVisible]);

  useEffect(() => {
    if (editDocumentoId) {
      setModalAddDocumentoVisible(true);
    }
  }, [editDocumentoId]);

  useEffect(() => {
    if (!modalAddDocumentoVisible) {
      setEditDocumentoId(undefined);
    }
  }, [modalAddDocumentoVisible]);

  return (
    <Container pb={8}>
      {modalCreatePastaVisible.visible ? (
        <ModalPasta
          isOpen={modalCreatePastaVisible.visible}
          onClose={() => {
            setEditPasta(undefined);
            setModalCreatePastaVisible({
              visible: false,
              documentoPaiID: selectedPastaId,
            });
          }}
          onSave={() => {
            setEditPasta(undefined);
            refetchPastas();
          }}
          useSavePasta={useSavePasta}
          pastaId={editPasta?.id}
          unidadeId={unidadeId}
          pasta={editPasta}
          documentoPaiId={modalCreatePastaVisible.documentoPaiID}
        />
      ) : null}

      {modalAddDocumentoVisible ? (
        <ModalDocumento
          isOpen={modalAddDocumentoVisible}
          onClose={() => setModalAddDocumentoVisible(false)}
          onSave={() => {
            refetchDocumentos();
          }}
          unidadeId={unidadeId}
          hideDestino={hideDestino}
          documentoId={editDocumentoId}
          documentoNome={
            editDocumentoId
              ? documentos?.items?.find((d) => d.id === editDocumentoId)!.nome
              : undefined
          }
          documentoPaiId={selectedPastaId}
          useSaveDocumento={useSaveDocumento}
        />
      ) : null}

      <Box>
        {midComponent ? (
          <PageHeader
            title={title}
            permissions={
              PermissaoEnum.Backoffice.Administrativo.Documento.Pasta.Criar
            }
            titleButton="Nova pasta"
            onClick={() =>
              setModalCreatePastaVisible({
                visible: true,
                documentoPaiID: selectedPastaId,
              })
            }
            midComponent={
              <Flex gridGap="8px">
                <Box minW="300px" maxW="250px">
                  <InputSearch
                    defaultValue={busca}
                    onSearch={(v) => setBusca(v || "")}
                    placeholder="Procurar por nome"
                    maxW="100%"
                    inputProps={{ bg: "white !important" }}
                  />
                </Box>
                <Box minW="250px" maxW="250px">
                  <SelectGrupoUnidade
                    value={gruposUnidadesIds}
                    onChange={setGrupoUnidadesIds}
                    mode="multiple"
                    maxTagCount={2}
                    bg="white"
                  />
                </Box>
                <Box minW="400px" maxW="300px">
                  <SelectUnidade
                    value={unidadesIds}
                    onChange={setUnidadesIds}
                    mode="multiple"
                    maxTagCount={1}
                    bg="white"
                  />
                </Box>
              </Flex>
            }
          />
        ) : (
          <PrimaryButton
            size="sm"
            permissions={
              PermissaoEnum.Backoffice.Administrativo.Documento.Pasta.Criar
            }
            onClick={() =>
              setModalCreatePastaVisible({
                visible: true,
                documentoPaiID: selectedPastaId,
              })
            }
          >
            Nova pasta
          </PrimaryButton>
        )}
      </Box>

      <BreadcrumbDocs breadCrumb={breadCrumb} onSelectPasta={onSelectPasta} />

      <Box mb={4}>
        <Pastas
          pastas={pastas}
          isLoading={_isLoadingPastas}
          selectedPasta={selectedPastaId}
          setSelectedPasta={onSelectPasta}
          onEdit={(id, nome, destinoId, tipoDestinoId) =>
            setEditPasta({ id, nome, destinoId, tipoDestinoId })
          }
          onDeleted={async () => {
            onSelectPasta(undefined);
            refetchPastas();
          }}
        />
      </Box>
      {pastas && selectedPastaId ? (
        <OutilineButton
          mb={4}
          permissions={[
            PermissaoEnum.Backoffice.Administrativo.Documento.Criar,
          ]}
          onClick={() => setModalAddDocumentoVisible(true)}
        >
          Novo documento
        </OutilineButton>
      ) : null}
      <Box>
        <Documentos
          documentos={documentos?.items ?? []}
          isLoading={_isLoadingDocumentos}
          nomePasta={nomePasta!}
          onEdit={setEditDocumentoId}
          onDelete={async (id) => {
            const { isConfirmed } = await showConfirm(
              "Deseja realmente deletar esse arquivo?"
            );

            if (isConfirmed) {
              deletarArquivo(id, {
                onError: (e) => showErrorAxios(e),
                onSuccess: () => {
                  refetchDocumentos();
                },
              });
            }
          }}
        />
      </Box>
      <Pagination
        style={{ marginTop: 16, marginLeft: "auto" }}
        current={documentos?.meta.currentPage ?? 1}
        total={documentos?.meta.totalItems ?? 0}
        pageSize={documentos?.meta.itemsPerPage ?? 0}
        onChange={(page, limit) => {
          setPagination({ page, limit });
        }}
      />
    </Container>
  );
}

export default ContentDocumentos;
